<template>
    <v-dialog v-model="isOpen" max-width="800" persistent>
        <v-card>
            <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-inbox-multiple</v-icon>
                Sperrgut-Regale verwalten
                <v-spacer></v-spacer>
                <v-btn icon dark @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-4">
                <!-- Loading state -->
                <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

                <!-- Slots list -->
                <div v-if="!loading" class="mb-4">
                    <div class="d-flex align-center mb-3">
                        <h3 class="text-h6">Verfügbare Sperrgut-Regale</h3>
                        <v-spacer></v-spacer>
                        <v-btn color="success" @click="openCreateDialog" small>
                            <v-icon left small>mdi-plus</v-icon>
                            Neues Regal
                        </v-btn>
                    </div>

                    <v-list v-if="slots.length > 0" outlined>
                        <v-list-item v-for="slot in slots" :key="slot.slot" class="slot-item">
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-bold">
                                    {{ slot.slot }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="mt-2">
                                    <v-chip
                                        v-for="table in slot.allowedTables"
                                        :key="table"
                                        x-small
                                        class="mr-1 mb-1"
                                        color="primary"
                                        outlined
                                    >
                                        {{ table }}
                                    </v-chip>
                                </v-list-item-subtitle>
                                <v-list-item-subtitle class="caption grey--text mt-1">
                                    Erstellt: {{ formatDate(slot.createdAt) }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <div class="d-flex gap-2">
                                    <v-tooltip bottom :disabled="!!printer">
                                        <template v-slot:activator="{ on, attrs }">
                                            <div v-bind="attrs" v-on="on">
                                                <v-btn
                                                    icon
                                                    small
                                                    @click="printLabel(slot)"
                                                    color="success"
                                                    :loading="printingSlots[slot.slot]"
                                                    :disabled="!printer"
                                                >
                                                    <v-icon small>mdi-printer</v-icon>
                                                </v-btn>
                                            </div>
                                        </template>
                                        <span>Bitte wählen Sie zuerst einen Drucker aus</span>
                                    </v-tooltip>
                                    <v-btn icon small @click="openEditDialog(slot)" color="primary">
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn icon small @click="confirmDelete(slot)" color="error">
                                        <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>

                    <v-alert v-else type="info" outlined>
                        Keine Sperrgut-Regale vorhanden. Klicken Sie auf "Neues Regal" um eines zu erstellen.
                    </v-alert>
                </div>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn @click="close" text>Schließen</v-btn>
            </v-card-actions>
        </v-card>

        <!-- Create/Edit Dialog -->
        <v-dialog v-model="editDialog" max-width="600" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">{{ isEditMode ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                    {{ isEditMode ? 'Regal bearbeiten' : 'Neues Regal erstellen' }}
                </v-card-title>

                <v-card-text class="pa-4">
                    <v-form ref="form" v-model="formValid">
                        <v-text-field
                            v-model="formData.slotName"
                            label="Regal-Name (z.B. MESSLATTE, ERINNERUNGSBOX)"
                            :rules="[rules.required, rules.nameFormat]"
                            :disabled="isEditMode"
                            outlined
                            dense
                            class="mb-2"
                            hint="Automatischer Präfix: SPERRGUT_"
                            persistent-hint
                            prefix="SPERRGUT_"
                        ></v-text-field>

                        <div class="mb-2">
                            <label class="text-body-2 font-weight-medium mb-1 d-block">
                                Erlaubte Tabellen
                            </label>
                            <v-combobox
                                v-model="formData.allowedTables"
                                :items="availableTables"
                                :rules="[rules.requiredArray]"
                                :loading="loadingTables"
                                label="Tabellen auswählen oder eingeben"
                                multiple
                                chips
                                deletable-chips
                                outlined
                                dense
                                small-chips
                            >
                                <template v-slot:selection="{ item }">
                                    <v-chip small close @click:close="removeTable(item)">
                                        {{ item }}
                                    </v-chip>
                                </template>
                            </v-combobox>
                        </div>

                        <v-alert v-if="formError" type="error" dense outlined class="mt-2">
                            {{ formError }}
                        </v-alert>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn @click="closeEditDialog" text>Abbrechen</v-btn>
                    <v-btn
                        @click="saveSlot"
                        color="primary"
                        :loading="saving"
                        :disabled="!formValid"
                    >
                        <v-icon left small>mdi-content-save</v-icon>
                        Speichern
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
                <v-card-title class="error white--text">
                    <v-icon left color="white">mdi-alert</v-icon>
                    Regal löschen?
                </v-card-title>

                <v-card-text class="pa-4">
                    <p>Möchten Sie das Regal <strong>{{ slotToDelete?.slot }}</strong> wirklich löschen?</p>
                    <p class="mb-0 caption grey--text">Diese Aktion kann nicht rückgängig gemacht werden.</p>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn @click="deleteDialog = false" text>Abbrechen</v-btn>
                    <v-btn @click="deleteSlot" color="error" :loading="deleting">
                        <v-icon left small>mdi-delete</v-icon>
                        Löschen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import PacklistService from '@/services/PacklistService';

export default {
    name: 'ExtraSlotsModal',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        printer: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            saving: false,
            deleting: false,
            loadingTables: false,
            printingSlots: {},
            slots: [],
            availableTables: [],
            editDialog: false,
            deleteDialog: false,
            isEditMode: false,
            formValid: false,
            formData: {
                slotName: '',
                allowedTables: []
            },
            formError: '',
            slotToDelete: null,
            rules: {
                required: v => !!v || 'Pflichtfeld',
                requiredArray: v => (v && v.length > 0) || 'Mindestens eine Tabelle erforderlich',
                nameFormat: v => /^[A-Z_]+$/.test(v) || 'Nur Großbuchstaben und Unterstriche erlaubt'
            }
        };
    },
    computed: {
        isOpen: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        }
    },
    watch: {
        isOpen(val) {
            if (val) {
                this.loadSlots();
                this.loadAvailableTables();
            }
        }
    },
    methods: {
        async loadSlots() {
            this.loading = true;
            try {
                const response = await PacklistService.getAllExtraSlots();
                this.slots = response.data.slots || [];
            } catch (error) {
                console.error('Error loading slots:', error);
                this.$emit('error', 'Fehler beim Laden der Regale');
            } finally {
                this.loading = false;
            }
        },

        async loadAvailableTables() {
            this.loadingTables = true;
            try {
                const response = await PacklistService.getAvailableProductTables();
                this.availableTables = response.data.tables || [];
            } catch (error) {
                console.error('Error loading available tables:', error);
                this.availableTables = [];
            } finally {
                this.loadingTables = false;
            }
        },

        openCreateDialog() {
            this.isEditMode = false;
            this.formData = {
                slotName: '',
                allowedTables: []
            };
            this.formError = '';
            this.editDialog = true;
        },

        openEditDialog(slot) {
            this.isEditMode = true;
            // Extract the name part after "SPERRGUT_"
            const slotName = slot.slot.replace(/^SPERRGUT_/, '');
            this.formData = {
                slotName: slotName,
                allowedTables: [...slot.allowedTables]
            };
            this.formError = '';
            this.editDialog = true;
        },

        closeEditDialog() {
            this.editDialog = false;
            this.formError = '';
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        async saveSlot() {
            if (!this.$refs.form.validate()) {
                return;
            }

            this.saving = true;
            this.formError = '';

            try {
                // Add SPERRGUT_ prefix to the name
                const fullSlotName = `SPERRGUT_${this.formData.slotName}`;

                if (this.isEditMode) {
                    await PacklistService.updateExtraSlot(fullSlotName, this.formData.allowedTables);
                    this.$emit('success', `Regal ${fullSlotName} aktualisiert`);
                } else {
                    await PacklistService.createExtraSlot(fullSlotName, this.formData.allowedTables);
                    this.$emit('success', `Regal ${fullSlotName} erstellt`);
                }

                this.closeEditDialog();
                await this.loadSlots();
            } catch (error) {
                console.error('Error saving slot:', error);
                this.formError = error.response?.data?.error || 'Fehler beim Speichern';
            } finally {
                this.saving = false;
            }
        },

        confirmDelete(slot) {
            this.slotToDelete = slot;
            this.deleteDialog = true;
        },

        async deleteSlot() {
            if (!this.slotToDelete) return;

            this.deleting = true;
            try {
                await PacklistService.deleteExtraSlot(this.slotToDelete.slot);
                this.$emit('success', `Regal ${this.slotToDelete.slot} gelöscht`);
                this.deleteDialog = false;
                this.slotToDelete = null;
                await this.loadSlots();
            } catch (error) {
                console.error('Error deleting slot:', error);
                this.$emit('error', error.response?.data?.error || 'Fehler beim Löschen');
            } finally {
                this.deleting = false;
            }
        },

        removeTable(table) {
            const index = this.formData.allowedTables.indexOf(table);
            if (index > -1) {
                this.formData.allowedTables.splice(index, 1);
            }
        },

        async printLabel(slot) {
            if (!this.printer || !this.printer.url) {
                this.$emit('error', 'Bitte wählen Sie zuerst einen Drucker aus');
                return;
            }

            this.$set(this.printingSlots, slot.slot, true);
            try {
                await PacklistService.printExtraSlotLabel(slot.slot, this.printer.url);
                this.$emit('success', `Label für ${slot.slot} wird gedruckt`);
            } catch (error) {
                console.error('Error printing label:', error);
                this.$emit('error', error.response?.data?.error || 'Fehler beim Drucken des Labels');
            } finally {
                this.$set(this.printingSlots, slot.slot, false);
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('de-DE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        close() {
            this.isOpen = false;
        }
    }
};
</script>

<style scoped>
.slot-item {
    border-bottom: 1px solid #e0e0e0;
}

.slot-item:last-child {
    border-bottom: none;
}

.gap-2 {
    gap: 8px;
}
</style>
